<template>
  <div class="flex flex-col items-center justify-center w-full h-full rounded-xl overflow-hidden relative pt-5">
    <!-- Loading state (image loading or searching) -->
    <div v-if="isLoading || isSearching" class="text-center text-theme-primary p-8">
      <div class="w-12 h-12 border-4 loading-spinner rounded-full animate-spin mx-auto mb-4"></div>
      <p v-if="isSearching">{{ t('image.searching') }}</p>
      <p v-else>{{ t('image.loading') }}</p>
    </div>

    <!-- Processing state (MQTT status monitoring or polling fallback) -->
    <div v-else-if="isProcessing || isPolling" class="text-center text-theme-primary p-8 max-w-[500px]">
      <div class="w-16 h-16 mx-auto mb-4 relative">
        <!-- Animated processing icon -->
        <svg class="w-16 h-16 animate-pulse text-theme-primary" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"
          fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="10" />
          <path d="M12 6v6l4 2" />
        </svg>
      </div>

      <h3 class="m-0 mb-2 text-2xl font-semibold">{{ t('status.processing') }}</h3>
      <p class="my-2 opacity-90 text-base">{{ statusMessage || t('status.monitoring') }}</p>

      <!-- Progress stages -->
      <div class="mt-6 space-y-3">
        <div v-for="stage in [1, 2, 3, 4]" :key="stage" class="flex items-center gap-3">
          <!-- Stage indicator -->
          <div class="w-8 h-8 rounded-full flex items-center justify-center shrink-0 transition-all duration-300"
            :class="currentStage >= stage
                ? 'bg-theme-primary text-theme-primary'
                : 'bg-theme-secondary text-theme-secondary'
              ">
            <svg v-if="currentStage > stage" class="w-5 h-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"
              fill="none" stroke="currentColor" stroke-width="3">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <span v-else class="text-sm font-bold">{{ stage }}</span>
          </div>

          <!-- Stage label -->
          <span class="text-sm text-left transition-all duration-300" :class="currentStage >= stage ? 'font-semibold text-theme-primary' : 'text-theme-secondary'
            ">
            {{ t(`status.stage${stage}`) }}
          </span>
        </div>
      </div>

      <!-- Progress bar -->
      <div v-if="progress > 0" class="mt-6 w-full bg-theme-primary rounded-full h-2 overflow-hidden">
        <div class="h-full bg-theme-progress transition-all duration-300 ease-out rounded-full"
          :style="{ width: `${progress}%` }"></div>
      </div>
    </div>

    <!-- Error state (only after polling exhausts all attempts) -->
    <div v-else-if="hasError && !isProcessing && !isPolling" class="text-center text-theme-primary p-8 max-w-[500px]">
      <svg class="w-16 h-16 mx-auto mb-4 opacity-90 text-theme-secondary" xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <circle cx="12" cy="12" r="10" stroke-width="2" />
        <line x1="12" y1="8" x2="12" y2="12" stroke-width="2" />
        <line x1="12" y1="16" x2="12.01" y2="16" stroke-width="2" />
      </svg>
      <h3 class="m-0 mb-2 text-2xl font-semibold">{{ t('image.errorTitle') }}</h3>
      <p class="my-2 mb-6 opacity-90 text-[0.95rem]">{{ errorMessage }}</p>
      <button v-if="imageUrl" @click="retryLoad"
        class="button-primary border-none py-3 px-6 rounded-lg font-semibold cursor-pointer transition-all duration-200 hover:transform hover:-translate-y-0.5 hover:shadow-[0_4px_12px_rgba(0,0,0,0.2)] active:translate-y-0">
        {{ t('image.retry') }}
      </button>
    </div>

    <!-- No image specified -->
    <div v-else-if="!imageUrl" class="text-center text-theme-primary p-8 max-w-[500px]">
      <svg class="w-20 h-20 mx-auto mb-6 opacity-80 text-theme-secondary" xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" stroke-width="2" />
        <circle cx="8.5" cy="8.5" r="1.5" stroke-width="2" />
        <polyline points="21 15 16 10 5 21" stroke-width="2" />
      </svg>
      <h3 class="m-0 mb-2 text-2xl font-semibold">{{ t('image.noImageTitle') }}</h3>
      <p class="my-2 opacity-90">{{ t('image.noImageText', { code: 'publicId' }) }}</p>
      <p class="my-2 opacity-90 mt-4 text-[0.9rem]">
        {{ t('image.noImageExample', { code: '?publicId=folder/image-name' }) }}
      </p>
    </div>

    <!-- Image display -->
    <div v-else
      class="w-full h-full flex flex-col items-start justify-start p-5 gap-5 overflow-y-auto overflow-x-hidden">
      <!-- Image -->
      <div class="shrink-0 w-full max-w-full flex items-center justify-center relative overflow-hidden rounded-lg">
        <img ref="imageElement" :src="imageUrl" :alt="alt" :class="[
          'w-full h-auto max-w-full block rounded-lg opacity-0 transition-opacity duration-300 ease-in-out',
          { 'opacity-100': imageLoaded }
        ]" @load="handleImageLoad" @error="handleImageError" loading="lazy" />
      </div>

      <!-- Privacy Notice -->
      <div class="shrink-0 w-auto bg-theme-primary-90 backdrop-blur-md rounded-lg p-4 border border-theme-default">
        <p class="m-0 text-theme-primary text-sm leading-6 text-center">
          {{ t('privacy.notice') }}
        </p>
      </div>

      <!-- Download/Share Button -->
      <button v-if="imageLoaded"
        class="shrink-0 flex items-center justify-center gap-3 w-full p-4 button-primary border-none rounded-xl text-base font-semibold cursor-pointer transition-all duration-200 ease-in-out shadow-[0_4px_12px_rgba(0,0,0,0.15)] disabled:opacity-60 disabled:cursor-not-allowed hover:not(:disabled):transform hover:not(:disabled):-translate-y-0.5 hover:not(:disabled):shadow-[0_6px_16px_rgba(0,0,0,0.2)] active:not(:disabled):translate-y-0 active:not(:disabled):shadow-[0_2px_8px_rgba(0,0,0,0.15)]"
        @click="handleDownload" :disabled="downloading" style="-webkit-tap-highlight-color: transparent">
        <svg v-if="!downloading" class="w-6 h-6 shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"
          fill="none" stroke="currentColor">
          <path v-if="canShare" d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8M16 6l-4 4-4-4M12 2v10" stroke-width="2"
            stroke-linecap="round" stroke-linejoin="round" />
          <path v-else d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" stroke-width="2"
            stroke-linecap="round" stroke-linejoin="round" />
        </svg>
        <div v-else class="w-6 h-6 border-[3px] loading-spinner rounded-full animate-spin"></div>
        <span>{{ downloadButtonText }}</span>
      </button>

      <!-- Website Button -->
      <a v-if="imageLoaded" href="https://www.eiopa.europa.eu/consumers_en" target="_blank" rel="noopener noreferrer"
        class="shrink-0 flex items-center justify-center gap-3 w-full p-4 button-primary border-none rounded-xl text-base font-semibold cursor-pointer transition-all duration-200 ease-in-out shadow-[0_4px_12px_rgba(0,0,0,0.15)] hover:not(:disabled):-translate-y-0.5"
        style="-webkit-tap-highlight-color: transparent">
        <svg class="w-6 h-6 shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" stroke-width="2">
          <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3" stroke-linecap="round"
            stroke-linejoin="round" />
        </svg>
        <span>{{ t('visitWebsite') }}</span>
      </a>


    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue'
import { useCloudinary } from '@composables/useCloudinary'
import { useLanguage } from '@composables/useLanguage'
import { useImageStatus } from '@composables/useImageStatus'

const props = defineProps({
  alt: {
    type: String,
    default: 'Cloudinary Image'
  }
})

// Composables
const { publicId, imageUrl, error: cloudinaryError, isSearching, searchMode } = useCloudinary()
const { t } = useLanguage()

// Get route to access query parameters
import { useRoute } from 'vue-router'
const route = useRoute()

// Image status monitoring (MQTT)
// Pass both publicId and filename for flexible MQTT topic subscription
const {
  isProcessing,
  isReady,
  currentStage,
  statusMessage,
  progress,
  error: statusError,
  startMonitoring,
  stopMonitoring
} = useImageStatus({
  publicId: publicId.value,
  filename: route.query.filename
})

// Component state
const imageElement = ref(null)
const imageLoaded = ref(false)
const isLoading = ref(false)
const loadError = ref(null)
const retryCount = ref(0)
const downloading = ref(false)
const canShare = ref(false)

// Polling state — used as fallback when MQTT/SSE is unavailable
const isPolling = ref(false)
let pollingTimer = null
let pollAttempts = 0
const MAX_POLL_ATTEMPTS = 36 // ~3 minutes at 5 s intervals

const showImageFromUrl = async (url) => {
  loadError.value = null
  imageLoaded.value = false
  retryCount.value = 0
  await nextTick()
  if (imageElement.value) {
    imageElement.value.src = url
  }
}

const stopPolling = () => {
  if (pollingTimer) {
    clearTimeout(pollingTimer)
    pollingTimer = null
  }
  isPolling.value = false
}

const schedulePoll = () => {
  pollingTimer = setTimeout(async () => {
    pollingTimer = null
    if (!imageUrl.value) { stopPolling(); return }

    if (pollAttempts >= MAX_POLL_ATTEMPTS) {
      stopPolling()
      loadError.value = 'Failed to load image'
      return
    }

    pollAttempts++
    const probe = new window.Image()
    const src = `${imageUrl.value}?t=${Date.now()}`
    probe.onload = async () => {
      stopPolling()
      await showImageFromUrl(src)
    }
    probe.onerror = () => schedulePoll()
    probe.src = src
  }, 5000)
}

const startPolling = () => {
  if (pollingTimer || isPolling.value) return
  pollAttempts = 0
  isPolling.value = true
  schedulePoll()
}

// Check if native share API is available
onMounted(() => {
  canShare.value = navigator.share !== undefined
})

onUnmounted(() => {
  stopPolling()
})

// Computed properties
const hasError = computed(() => !!loadError.value || !!cloudinaryError.value)
const errorMessage = computed(() => {
  if (cloudinaryError.value) return cloudinaryError.value
  if (loadError.value) return t('image.errorText')
  return ''
})

// When MQTT signals the image is ready, stop polling and auto-fetch
watch(isReady, async (ready) => {
  if (!ready) return
  stopPolling()
  await showImageFromUrl(`${imageUrl.value}?t=${Date.now()}`)
})

/**
 * Handle successful image load
 */
const handleImageLoad = () => {
  isLoading.value = false
  imageLoaded.value = true
  loadError.value = null
  retryCount.value = 0
  stopPolling()
}

/**
 * Handle image load error
 * @param {Event} event - Error event
 */
const handleImageError = event => {
  if (import.meta.env.DEV) {
    console.error('Failed to load image:', {
      publicId: publicId.value,
      url: imageUrl.value,
      error: event
    })
  }

  isLoading.value = false
  imageLoaded.value = false

  if (publicId.value) {
    // MQTT fast path — shows processing screen immediately
    startMonitoring()
    // Polling fallback — keeps checking every 5 s if MQTT/SSE is unavailable
    startPolling()
    // Do not set loadError here; polling keeps the processing screen visible
  } else {
    loadError.value = 'Failed to load image'
  }
}

/**
 * Retry loading the image
 */
const retryLoad = async () => {
  if (retryCount.value >= 3) {
    loadError.value = t('image.maxRetries')
    return
  }

  retryCount.value++
  loadError.value = null
  imageLoaded.value = false
  // Do NOT set isLoading = true here: the <img> lives in the v-else block and is
  // unmounted whenever isLoading/hasError is true, so imageElement.value would be
  // null and the src update would silently do nothing.

  // Wait for Vue to re-render the v-else block (which mounts the <img> element)
  await nextTick()

  if (imageElement.value) {
    const baseUrl = imageUrl.value || imageElement.value.src.split('?')[0]
    // Cache-bust so the browser re-fetches even if it cached a 404
    imageElement.value.src = `${baseUrl}?retry=${retryCount.value}&t=${Date.now()}`
  }
}

/**
 * Download or share the image
 */
const handleDownload = async () => {
  if (!imageUrl.value || downloading.value) return

  downloading.value = true

  try {
    // Fetch the image as a blob
    const response = await fetch(imageUrl.value)
    const blob = await response.blob()

    // Use native share API on mobile devices
    if (canShare.value && navigator.share) {
      const file = new File([blob], `${publicId.value || 'image'}.jpg`, { type: blob.type })

      await navigator.share({
        title: t('download.shareTitle'),
        text: t('download.shareText'),
        files: [file]
      })
      if (import.meta.env.DEV) {
        console.log('Image shared successfully')
      }
    } else {
      // Fallback: Direct download for desktop
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `${publicId.value || 'image'}.jpg`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
      if (import.meta.env.DEV) {
        console.log('Image downloaded successfully')
      }
    }
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error('Error downloading/sharing image:', error)
    }
    alert(t('image.errorDownload'))
  } finally {
    downloading.value = false
  }
}

// Computed property for button text
const downloadButtonText = computed(() => {
  if (downloading.value) return t('download.loading')
  return canShare.value ? t('download.buttonShare') : t('download.buttonDownload')
})
</script>

<style scoped>
.button-primary {
  background-color: var(--theme-button-primary-bg);
  color: var(--theme-button-primary-text);
  border-color: var(--theme-button-primary-border);
}

.button-primary:hover {
  background-color: var(--theme-button-primary-bg-hover);
}

.loading-spinner {
  border-color: color-mix(in srgb, var(--color-theme-text-inverted) 30%, transparent);
  border-top-color: var(--color-theme-text-inverted);
}

.text-theme-primary {
  color: var(--color-theme-primary-text);
}

.text-theme-secondary {
  color: var(--color-theme-secondary-text);
}

.bg-theme-progress {
  background-color: var(--color-theme-primary);
}

.bg-theme-primary {
  background-color: var(--color-theme-bg-primary);
}

.bg-theme-primary-90 {
  background-color: color-mix(in oklab, var(--color-theme-bg-primary) 90%, transparent);
}

.bg-theme-secondary {
  background-color: var(--color-theme-bg-secondary);
}

.border-theme-default {
  border-color: var(--color-theme-border);
}
</style>
